import { Component, Input, OnInit, OnChanges, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { ExamenService } from '../../services/examen.service';
import { SharedService } from '../../services/shared.service';
import { Examen } from '../../models/examen.model';
import { Graduacion } from '../../models/graduacion.model';
import { Estudiante } from '../../models/estudiante.model';

@Component({
  selector: 'app-examenes-list',
  templateUrl: './examenes-list.component.html',
  styleUrls: ['./examenes-list.component.css']
})
export class ExamenesListComponent implements OnInit, OnChanges {
  @Input() nroLibreta: number | null = null;
  @Input() isEstudianteEditMode: boolean = false;
  @Input() estudiante: Estudiante | null = null;
  @ViewChild('examenesListContainer') examenesListContainer!: ElementRef;
  
  examenes: Examen[] = [];
  graduaciones: Graduacion[] = [];
  availableGraduaciones: Graduacion[] = []; // Filtered list without BLANCO
  graduacionActual: Graduacion | null = null;
  isLoading = true;
  errorMessage: string | null = null;
  
  // Add belt skipping detection properties
  beltSkippingWarnings: Map<number, string> = new Map(); // Map examen ID to warning message

  constructor(
    private examenService: ExamenService,
    private sharedService: SharedService
  ) {}

  ngOnInit(): void {
    this.sharedService.graduaciones$.subscribe(
      (data) => {
        this.graduaciones = data;
        // Filter out BLANCO (id: 1) and sort by ID ascending (gups first, then dans)
        this.availableGraduaciones = data
          .filter(g => g.id !== 1) // Remove BLANCO
          .sort((a, b) => a.id - b.id); // Sort by ID ascending
      },
      (err) => console.error('Error fetching graduaciones:', err)
    );

    if (this.nroLibreta) {
      this.fetchExamenes();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.nroLibreta && !changes.nroLibreta.firstChange) {
      this.fetchExamenes();
    }
  }

  getGraduacionActual(): Graduacion | null {
    if (!this.examenes || this.examenes.length === 0 || !this.graduaciones || this.graduaciones.length === 0) {
      return null;
    }

    // Find the exam with the highest graduation ID (most recent belt)
    const ultimoExamen = this.examenes.reduce((latest, current) => {
      return current.idGraduacion > latest.idGraduacion ? current : latest;
    });

    // Find the corresponding graduation object
    const graduacionActual = this.graduaciones.find(g => g.id === ultimoExamen.idGraduacion) || null;
    
    return graduacionActual;
  }

  // Update the fetchExamenes method to set loading state properly
  fetchExamenes(): void {
    this.errorMessage = null;
    this.isLoading = true; // Set loading to true when starting to fetch
    
    if (this.nroLibreta) {
      this.examenService.getExamenesByEstudiante(this.nroLibreta).subscribe(
        (examenes) => {
          this.examenes = examenes || [];
          // Normalize dates received from the server
          this.examenes.forEach(examen => {
            if (examen.fcExamen) {
              examen.fcExamen = this.sharedService.normalizeDate(examen.fcExamen);
            }
          });
          this.sortExamenes();
          this.isLoading = false; // Set loading to false after processing
          
          if (this.examenes.length === 0) {
            this.addExamen();
          }
        },
        (err) => {
          this.isLoading = false; // Set loading to false on error
          
          if (err.status === 404) {
            this.errorMessage = 'El estudiante no tiene examenes registrados';
            this.examenes = [];
            this.addExamen();
          } else {
            this.errorMessage = 'Error al buscar examenes';
            console.error('Error fetching examenes:', err);
          }
        }
      );
    }
  }

  private sortExamenes(): void {
    this.examenes.sort((a, b) => {
      // Sort by graduation ID descending (highest belt first), then by date descending (most recent first)
      if (a.idGraduacion !== b.idGraduacion) {
        return b.idGraduacion - a.idGraduacion;
      }
      
      // If same graduation, sort by date (most recent first)
      if (a.fcExamen && b.fcExamen) {
        const dateA = new Date(a.fcExamen);
        const dateB = new Date(b.fcExamen);
        return dateB.getTime() - dateA.getTime();
      }
      
      // Handle cases where one or both dates are missing
      if (a.fcExamen && !b.fcExamen) return -1;
      if (!a.fcExamen && b.fcExamen) return 1;
      
      return 0;
    });
  }

  toggleEditMode(examen: Examen): void {
    if (examen.id === 0 && examen.isEditMode) {
      this.cancelAddExamen(examen);
    } else {
      examen.isEditMode = !examen.isEditMode;
    }
  }

  addExamen(): void {
    const today = this.sharedService.formatDate(new Date().toISOString());
    const nroLibreta = this.nroLibreta !== null ? this.nroLibreta : 0;
    
    const currentGraduacion = this.getGraduacionActual();
    let nextGraduacionId: number;
    
    if (!currentGraduacion) {
      // First exam - start at PUNTA AMARILLA (id: 2)
      nextGraduacionId = 2;
    } else {
      // Next belt in sequence
      nextGraduacionId = currentGraduacion.id + 1;
      
      // Check if we've reached the end of available graduations
      const maxAvailableId = Math.max(...this.availableGraduaciones.map(g => g.id));
      if (nextGraduacionId > maxAvailableId) {
        nextGraduacionId = maxAvailableId;
        this.errorMessage = 'No hay graduaciones disponibles para agregar un nuevo examen.';
      }
    }
    
    const newExamen = new Examen(0, nroLibreta, nextGraduacionId, today, true, true);
    this.examenes.unshift(newExamen);
    newExamen.isEditMode = true;
    
    // Check for belt skipping
    this.checkBeltSkipping(newExamen);
    
    setTimeout(() => {
      this.focusOnNewExamDate();
    });
  }

  checkBeltSkipping(examen: Examen): void {
    if (!examen || !examen.idGraduacion || this.availableGraduaciones.length === 0) {
      this.beltSkippingWarnings.delete(examen.id);
      return;
    }

    // Get the highest graduation ID from EXISTING saved exams (excluding the current exam being edited)
    const existingExamenes = this.examenes.filter(e => e !== examen && e.id !== 0);
    
    let currentHighestGradId = 1; // Start with 1 (BLANCO - the implicit starting belt)
    
    if (existingExamenes.length > 0) {
      currentHighestGradId = Math.max(...existingExamenes.map(e => e.idGraduacion));
    }
    
    const selectedGradId = examen.idGraduacion;

    // Check if we're skipping belts (selected is more than 1 position ahead of the highest existing)
    if (selectedGradId > currentHighestGradId + 1) {
      // Find graduations that are between the highest existing and the selected one
      // Use availableGraduaciones (which excludes BLANCO) for the warning message
      const skippedGraduations = this.availableGraduaciones.filter(g => 
        g.id > currentHighestGradId && g.id < selectedGradId
      );
      
      if (skippedGraduations.length > 0) {
        const skippedBeltNames = skippedGraduations.map(g => g.nombre).join(', ');
        const warningMessage = `Saltando: ${skippedBeltNames}`;
        this.beltSkippingWarnings.set(examen.id, warningMessage);
      } else {
        this.beltSkippingWarnings.delete(examen.id);
      }
    } else {
      this.beltSkippingWarnings.delete(examen.id);
    }
  }

  getGraduacionForDisplay(): Graduacion | null {
    if (!this.examenes || this.examenes.length === 0 || !this.graduaciones || this.graduaciones.length === 0) {
      return null;
    }

    // Find the highest graduation ID from ALL exams (including unsaved ones being edited)
    const allExamenes = this.examenes.filter(e => e.idGraduacion > 0); // Only consider exams with valid graduation IDs
    
    if (allExamenes.length === 0) {
      return null;
    }

    const highestGradId = Math.max(...allExamenes.map(e => e.idGraduacion));
    
    // Find the corresponding graduation object
    const graduacionForDisplay = this.graduaciones.find(g => g.id === highestGradId) || null;
    
    return graduacionForDisplay;
  }

  // Update the onGraduacionChange method to trigger display update
  onGraduacionChange(examen: Examen): void {
    this.checkBeltSkipping(examen);
  }

  // Get belt skipping warning for an exam
  getBeltSkippingWarning(examen: Examen): string | null {
    return this.beltSkippingWarnings.get(examen.id) || null;
  }

  // New method to focus on the date field of the newly added exam
  private focusOnNewExamDate(): void {
    const dateInputs = this.examenesListContainer?.nativeElement.querySelectorAll('input[type="date"].new-exam-date');
    if (dateInputs && dateInputs.length > 0) {
      dateInputs[0].focus();
    }
  }

  getGraduacionObject(idGraduacion: number): Graduacion | null {
    return this.graduaciones.find(g => g.id === idGraduacion) || null;
  }

  cancelAddExamen(examen: Examen): void {
    this.beltSkippingWarnings.delete(examen.id);
    this.examenes = this.examenes.filter(e => e !== examen);
  }

  saveChanges(examen: Examen, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const validationError = this.validateExamenDate(examen);
    if (validationError) {
      this.errorMessage = validationError;
      return;
    }

    // Simple confirmation for belt skipping
    const beltWarning = this.getBeltSkippingWarning(examen);
    if (beltWarning && examen.id === 0) {
      if (!confirm(`${beltWarning}. ¿Continuar?`)) {
        return;
      }
    }

    examen.disableValidation = false;

    if (examen.fcExamen) {
      examen.fcExamen = this.sharedService.formatDate(examen.fcExamen);
    }

    if (examen.id === 0) {
      this.examenService.addExamen(examen).subscribe(
        (data) => {
          examen.id = data.id;
          examen.isEditMode = false;
          this.beltSkippingWarnings.delete(examen.id);
          this.sortExamenes();
          this.errorMessage = null;
          
          // The display will automatically update since we're using getGraduacionForDisplay()
        },
        (err) => {
          console.error('Error adding examen:', err);
          this.errorMessage = err.error?.error || 'Error al crear el examen';
        }
      );
    } else {
      this.examenService.updateExamen(examen).subscribe(
        (data) => {
          examen.isEditMode = false;
          this.beltSkippingWarnings.delete(examen.id);
          this.sortExamenes();
          this.errorMessage = null;
          
          // The display will automatically update since we're using getGraduacionForDisplay()
        },
        (err) => {
          console.error('Error updating examen:', err);
          this.errorMessage = err.error?.error || 'Error al actualizar el examen';
        }
      );
    }
  }

  private validateExamenDate(examen: Examen): string | null {
    if (!examen.fcExamen) {
      return 'La fecha del examen es obligatoria';
    }

    const examDate = new Date(examen.fcExamen);
    
    // Validate that exam date is not in the future
    const today = new Date();
    if (examDate > today) {
      return 'La fecha del examen no puede ser en el futuro';
    }
    
    // Check that exam date is not before estudiante's fechaInicio or fechaNacimiento
    if (this.estudiante) {
      if (this.estudiante.fcInicio) {
        const inicioDate = new Date(this.estudiante.fcInicio);
        if (examDate < inicioDate) {
          return 'La fecha del examen no puede ser anterior a la fecha de inicio del estudiante';
        }
      }
      
      if (this.estudiante.fcNacimiento) {
        const nacimientoDate = new Date(this.estudiante.fcNacimiento);
        if (examDate < nacimientoDate) {
          return 'La fecha del examen no puede ser anterior a la fecha de nacimiento del estudiante';
        }
      }
    }
    
    // Check if the exam date fits in the belt progression sequence
    const otherExamenes = this.examenes.filter(e => e !== examen);
    if (otherExamenes.length > 0) {
      // Sort other exams by graduacion ID
      const sortedExams = [...otherExamenes].sort((a, b) => a.idGraduacion - b.idGraduacion);
      
      // Find exams with lower and higher graduacion IDs
      const lowerExams = sortedExams.filter(e => e.idGraduacion < examen.idGraduacion);
      const higherExams = sortedExams.filter(e => e.idGraduacion > examen.idGraduacion);
      
      // The latest lower exam date should be before this exam date
      if (lowerExams.length > 0) {
        const latestLowerExam = lowerExams.reduce((latest, current) => {
          const latestDate = latest.fcExamen ? new Date(latest.fcExamen) : new Date(0);
          const currentDate = current.fcExamen ? new Date(current.fcExamen) : new Date(0);
          return currentDate > latestDate ? current : latest;
        }, lowerExams[0]);
        
        if (latestLowerExam.fcExamen) {
          const latestLowerDate = new Date(latestLowerExam.fcExamen);
          if (examDate < latestLowerDate) {
            // Get graduation description for better error message
            const lowerGrad = this.graduaciones.find(g => g.id === latestLowerExam.idGraduacion);
            const currentGrad = this.graduaciones.find(g => g.id === examen.idGraduacion);
            return `La fecha de examen para ${currentGrad ? currentGrad.nombre : 'este cinturon'} no puede ser anterior a la fecha de examen para ${lowerGrad ? lowerGrad.nombre : 'un cinturon inferior'} (${latestLowerExam.fcExamen})`;
          }
        }
      }
      
      // The earliest higher exam date should be after this exam date
      if (higherExams.length > 0) {
        const earliestHigherExam = higherExams.reduce((earliest, current) => {
          const earliestDate = earliest.fcExamen ? new Date(earliest.fcExamen) : new Date(9999, 11, 31);
          const currentDate = current.fcExamen ? new Date(current.fcExamen) : new Date(9999, 11, 31);
          return currentDate < earliestDate ? current : earliest;
        }, higherExams[0]);
        
        if (earliestHigherExam.fcExamen) {
          const earliestHigherDate = new Date(earliestHigherExam.fcExamen);
          if (examDate > earliestHigherDate) {
            // Get graduation description for better error message
            const higherGrad = this.graduaciones.find(g => g.id === earliestHigherExam.idGraduacion);
            const currentGrad = this.graduaciones.find(g => g.id === examen.idGraduacion);
            return `La fecha del examen para ${currentGrad ? currentGrad.nombre : 'este cinturon'} no puede ser posterior a la fecha de examen de ${higherGrad ? higherGrad.nombre : 'un cinturon superior'} (${earliestHigherExam.fcExamen})`;
          }
        }
      }
    }
    
    return null; // No validation errors
  }

  getGraduacionDescripcion(idGraduacion: number): string {
    // Ensure we have graduaciones loaded
    if (this.graduaciones.length === 0) {
      return 'Cargando...';
    }
    
    const graduacion = this.graduaciones.find(g => g.id === Number(idGraduacion));
    if (!graduacion) {
      console.warn(`Graduation with ID ${idGraduacion} not found in list:`, this.graduaciones);
      return `Graduación ${idGraduacion} no encontrada`;
    }
    
    return graduacion.nombre;
  }
}